import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {isEmpty} from "@pg-mono/nodash";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {EmailInputWithVerification, useEmailVerificationApiError} from "@pg-rp/email-with-verification";

import {IRPStore} from "../../../app/rp_reducer";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormExtendedDefaultValues} from "../../constants/register_form_extended_default_values";
import {registerFormExtendedValidationSchema} from "../../constants/register_form_extended_validation_schema";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {AuthActionType} from "../../types/AuthActionType";
import type {IRegisterExtended} from "../../types/IRegisterExtended";
import {RegisterFormPhoneVerificationInfo} from "./RegisterFormPhoneVerificationInfo";

interface IProps {
    onAuthSuccess: (authActionType: AuthActionType) => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    registerButtonText?: string;
    gtmEventData?: () => void;
}

export const ExtendedRegisterForm = (props: IProps) => {
    const {onAuthSuccess, userSource, gtmSourceSection, registerButtonText = "ZAŁÓŻ KONTO", gtmEventData} = props;

    const dispatch = useAppDispatch();
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});

    const formRef = useRef<HTMLFormElement>(null);

    const methods = useFormRHF<IRegisterExtended>({
        defaultValues: registerFormExtendedDefaultValues,
        resolver: yupResolver(registerFormExtendedValidationSchema)
    });
    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const disableSubmitButton =
        (!ignoreVerification && !data) || methods.formState.isSubmitting || !isEmpty(methods.formState.errors) || isLoading || isFetching;

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError]);

    const handleFieldFocus = () => {
        formRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    };

    const onSubmit = async (data: IRegisterExtended) => {
        await emailSignUp({
            email: data.email,
            password: data.password,
            name: data.name,
            phone: `${data.phone.code} ${data.phone.number}`,
            type: UserAccountType.LOCAL,
            source: userSource,
            marketing_email: data.marketing_email,
            marketing_sms: data.marketing_sms
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );

                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onAuthSuccess(AuthActionType.REGISTRATION);
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegisterExtended, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <div className={formWrapperStyle}>
                <form ref={formRef} className={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
                    <InputField name="name" placeholder="Imię i nazwisko" dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT} onFocus={handleFieldFocus} />
                    <EmailInputWithVerification
                        verificationData={data}
                        verificationTrigger={setEmail}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT}
                        onFocus={handleFieldFocus}
                    />
                    <InputField name="password" placeholder="Hasło" type="password" onFocus={handleFieldFocus} />
                    <PhoneFieldControlled
                        name="phone"
                        codes={countryCodes}
                        placeholder="Telefon"
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        afterInput={<RegisterFormPhoneVerificationInfo />}
                    />
                    <UserMktOptins showForm initialValues={registerFormExtendedDefaultValues} formId={`${currentViewType}_register_extended`} />

                    <div className={buttonWrapperStyle}>
                        <Button
                            className={submitButtonStyle}
                            type="submit"
                            variant="filled_primary"
                            isLoading={methods.formState.isSubmitting || isLoading || isFetching}
                            disabled={disableSubmitButton}
                        >
                            {registerButtonText}
                        </Button>
                    </div>
                </form>
            </div>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const buttonWrapperStyle = css({
    position: "sticky",
    backgroundColor: "var(--colors-white)",
    boxShadow: "0 -4px 6px -1px rgba(255, 255, 255, 0.8)",
    paddingTop: 2,
    paddingBottom: 2,
    zIndex: 10,
    bottom: 0,
    md: {
        bottom: "calc(-4 * var(--globals-baseUnit))"
    }
});

const submitButtonStyle = css({
    width: "100%"
});
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {isEmpty} from "@pg-mono/nodash";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {EmailInputWithVerification, useEmailVerificationApiError} from "@pg-rp/email-with-verification";

import {IRPStore} from "../../../app/rp_reducer";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {UserMktOptins} from "../../../user/components/UserMktOptins";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormExtendedDefaultValues} from "../../constants/register_form_extended_default_values";
import {registerFormExtendedValidationSchema} from "../../constants/register_form_extended_validation_schema";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {AuthActionType} from "../../types/AuthActionType";
import type {IRegisterExtended} from "../../types/IRegisterExtended";
import {RegisterFormPhoneVerificationInfo} from "./RegisterFormPhoneVerificationInfo";

interface IProps {
    onAuthSuccess: (authActionType: AuthActionType) => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    registerButtonText?: string;
    gtmEventData?: () => void;
}

export const ExtendedRegisterForm = (props: IProps) => {
    const {onAuthSuccess, userSource, gtmSourceSection, registerButtonText = "ZAŁÓŻ KONTO", gtmEventData} = props;

    const dispatch = useAppDispatch();
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});

    const formRef = useRef<HTMLFormElement>(null);

    const methods = useFormRHF<IRegisterExtended>({
        defaultValues: registerFormExtendedDefaultValues,
        resolver: yupResolver(registerFormExtendedValidationSchema)
    });
    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const disableSubmitButton =
        (!ignoreVerification && !data) || methods.formState.isSubmitting || !isEmpty(methods.formState.errors) || isLoading || isFetching;

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError]);

    const handleFieldFocus = () => {
        formRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    };

    const onSubmit = async (data: IRegisterExtended) => {
        await emailSignUp({
            email: data.email,
            password: data.password,
            name: data.name,
            phone: `${data.phone.code} ${data.phone.number}`,
            type: UserAccountType.LOCAL,
            source: userSource,
            marketing_email: data.marketing_email,
            marketing_sms: data.marketing_sms
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );

                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onAuthSuccess(AuthActionType.REGISTRATION);
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegisterExtended, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <div className={formWrapperStyle}>
                <form ref={formRef} className={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
                    <InputField name="name" placeholder="Imię i nazwisko" dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT} onFocus={handleFieldFocus} />
                    <EmailInputWithVerification
                        verificationData={data}
                        verificationTrigger={setEmail}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT}
                        onFocus={handleFieldFocus}
                    />
                    <InputField name="password" placeholder="Hasło" type="password" onFocus={handleFieldFocus} />
                    <PhoneFieldControlled
                        name="phone"
                        codes={countryCodes}
                        placeholder="Telefon"
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        afterInput={<RegisterFormPhoneVerificationInfo />}
                    />
                    <UserMktOptins showForm initialValues={registerFormExtendedDefaultValues} formId={`${currentViewType}_register_extended`} />

                    <div className={buttonWrapperStyle}>
                        <Button
                            className={submitButtonStyle}
                            type="submit"
                            variant="filled_primary"
                            isLoading={methods.formState.isSubmitting || isLoading || isFetching}
                            disabled={disableSubmitButton}
                        >
                            {registerButtonText}
                        </Button>
                    </div>
                </form>
            </div>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const buttonWrapperStyle = css({
    position: "sticky",
    backgroundColor: "var(--colors-white)",
    boxShadow: "0 -4px 6px -1px rgba(255, 255, 255, 0.8)",
    paddingTop: 2,
    paddingBottom: 2,
    zIndex: 10,
    bottom: 0,
    md: {
        bottom: "calc(-4 * var(--globals-baseUnit))"
    }
});

const submitButtonStyle = css({
    width: "100%"
});
